import React, { useState } from 'react';
import StatusBadge from '../shared/StatusBadge';
import A2PBrandModal from './A2PBrandModal';
import './SmsComplianceCards.css';

const A2PBrandCard = ({ userId, brandData, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasBrand = brandData && brandData.id;
  const isBrandApproved = brandData?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(brandData.status);

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { badge: 'draft', label: 'Draft' },
      'pending': { badge: 'pending', label: 'Pending Review' },
      'pending-review': { badge: 'pending', label: 'Pending Review' },
      'in-review': { badge: 'pending', label: 'In Review' },
      'pending_trusthub': { badge: 'pending', label: 'Business Profile in Review' },
      'pending_a2p_trust_product': { badge: 'pending', label: 'Trust Product in Review' },
      'approved': { badge: 'approved', label: 'Approved' },
      'twilio-approved': { badge: 'approved', label: 'Approved' },
      'verified': { badge: 'approved', label: 'Approved' },
      'rejected': { badge: 'rejected', label: 'Rejected' },
      'twilio-rejected': { badge: 'rejected', label: 'Rejected' },
      'failed': { badge: 'rejected', label: 'Failed' },
    };
    return statusMap[status] || { badge: 'draft', label: status || 'Unknown' };
  };

  const getHeaderColorClass = (status) => {
    const normalizedStatus = status ? status.toLowerCase() : '';
    if (['twilio-approved', 'approved', 'verified'].includes(normalizedStatus)) return 'card-header--approved';
    if (['pending-review', 'pending', 'in-review', 'pending_trusthub', 'pending_a2p_trust_product'].includes(normalizedStatus)) return 'card-header--pending';
    if (['twilio-rejected', 'rejected', 'failed'].includes(normalizedStatus)) return 'card-header--rejected';
    if (['draft'].includes(normalizedStatus)) return 'card-header--draft';
    return 'card-header--default';
  };

  const getButtonText = () => {
    if (!hasBrand) return 'Register Brand';
    if (isBrandApproved) return 'View';
    return 'Edit Brand';
  };

  const handleModalClose = async (updated) => {
    setShowModal(false);
    if (updated) {
      setIsRefreshing(true);
      await onUpdate();
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1500);
    }
  };

  return (
    <>
      <div className="sms-compliance-card">
        {/* Header with Icon and Title */}
        <div className={`card-header ${hasBrand ? getHeaderColorClass(brandData.status) : ''}`}>
          <div className="card-icon-title">
            {/* <i className="la la-building" style={{ fontSize: '20px', color: '#087ab6',fontFamily:'LineAwesome' }}></i> */}
            <h3 className="card-title">A2P Brand Registration</h3>
          </div>
          {hasBrand && (
            <span className="status-text">{getStatusBadge(brandData.status).label}</span>
          )}
        </div>

        {/* Card Body with Message and Button */}
        <div className="card-body" style={{ padding: '30px 20px', textAlign: 'center' }}>
          {/* Status Message */}
          {hasBrand ? (
            <>
              {isBrandApproved && (
                <p style={{ color: '#28a745', marginBottom: '20px', fontSize: '14px' }}>
                  Brand approved - You can now create SMS campaigns
                </p>
              )}

              {['pending', 'pending-review', 'in-review'].includes(brandData.status) && (
                <p style={{ color: '#ffc107', marginBottom: '20px', fontSize: '14px' }}>
                  Pending review (typically 1-3 business days)
                </p>
              )}

              {brandData.status === 'pending_trusthub' && (
                <p style={{ color: '#ffc107', marginBottom: '20px', fontSize: '14px' }}>
                  Waiting for your business profile to be approved by Twilio
                </p>
              )}

              {brandData.status === 'pending_a2p_trust_product' && (
                <p style={{ color: '#ffc107', marginBottom: '20px', fontSize: '14px' }}>
                  Waiting for your A2P Trust Product to be approved by Twilio
                </p>
              )}

              {['rejected', 'twilio-rejected'].includes(brandData.status) && (
                <p style={{ color: '#dc3545', marginBottom: '20px', fontSize: '14px' }}>
                  Registration rejected. Please review and resubmit.
                </p>
              )}

              {brandData.status === 'failed' && (
                <p style={{ color: '#dc3545', marginBottom: '20px', fontSize: '14px' }}>
                  Registration failed. Please try again or contact support.
                </p>
              )}

              {brandData.status === 'draft' && (
                <p style={{ color: '#6c757d', marginBottom: '20px', fontSize: '14px' }}>
                  Draft saved. Complete and submit for review.
                </p>
              )}
            </>
          ) : (
            <p style={{ color: '#6c757d', marginBottom: '20px', fontSize: '14px' }}>
              Register your brand to enable SMS campaigns
            </p>
          )}

          {/* Button */}
          <button
            className="btn linear-gradient zy-save-btn"
            onClick={() => setShowModal(true)}
          >
            {getButtonText()}
          </button>

          {/* Refreshing Overlay */}
          {isRefreshing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              borderRadius: '12px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="ui active inline loader" style={{ marginBottom: '10px' }}></div>
                <p style={{ color: '#087ab6', fontSize: '13px', fontWeight: 500, margin: 0 }}>
                  Refreshing data...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* A2P Brand Modal */}
      {showModal && (
        <A2PBrandModal
          userId={userId}
          brandData={brandData}
          isEditMode={hasBrand}
          isApproved={isBrandApproved}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default A2PBrandCard;
